/**
 * MissionTriggerCard
 *
 * Renders a suggestion card when a parent mission triggers a child mission.
 * Shows the child mission name and provides a link to open it.
 */

import React from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@agion/ui'
import { ExternalLink, Zap } from 'lucide-react'

interface MissionTriggerCardProps {
  childMissionId: string
  childMissionName?: string
  childExecutionId: string
  onOpenMission?: (missionId: string, executionId: string) => void
}

export const MissionTriggerCard: React.FC<MissionTriggerCardProps> = ({
  childMissionId,
  childMissionName,
  childExecutionId,
  onOpenMission,
}) => {
  const { t } = useTranslation('chat')
  const displayName = childMissionName || childMissionId.slice(0, 8)

  return (
    <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10 p-3">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
          <Zap className="w-4 h-4 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900 dark:text-white">
            {t('trigger.continueWith', { name: displayName })}
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            {t('trigger.childMissionLaunched')}
          </p>
        </div>
        {onOpenMission && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenMission(childMissionId, childExecutionId)}
            className="flex-shrink-0"
          >
            <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
            {t('trigger.openMission')}
          </Button>
        )}
      </div>
    </div>
  )
}

export default MissionTriggerCard
